import { useAiSlidePanel } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import classNames from "classnames";
import { ChevronRight, Moon, Sun } from "lucide-react";
import { Fragment, memo, useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import type { ProductEntry, ProductStep } from "../../products";
import { AiStarIcon } from "../ui/ai-star-icon";
import { ExitOnboardingButton } from "../ui/exit-onboarding-button";
import * as styles from "./onboarding-header.module.css";

interface NavItem {
  label: string;
  key: string;
}

function deriveNavItems(steps: ProductStep[]): {
  items: NavItem[];
  stepToNavIndex: Map<string, number>;
} {
  const useGroups = steps.some((s) => s.navGroup);
  const items: NavItem[] = [];
  const stepToNavIndex = new Map<string, number>();

  if (useGroups) {
    const seen = new Set<string>();
    for (const step of steps) {
      if (step.hideFromNav || step.shouldSkip?.()) continue;
      const group = step.navGroup;
      if (!group) continue;
      if (!seen.has(group)) {
        seen.add(group);
        items.push({ label: group, key: `group-${group}` });
      }
      stepToNavIndex.set(step.path, items.length - 1);
    }
  } else {
    for (const step of steps) {
      if (step.hideFromNav || step.shouldSkip?.()) continue;
      stepToNavIndex.set(step.path, items.length);
      items.push({ label: step.label, key: step.id });
    }
  }

  return { items, stepToNavIndex };
}

export const ProductHeader = memo(function ProductHeader({
  product,
  storeName = "My Store",
  logoUrl,
}: {
  product: ProductEntry;
  storeName?: string;
  logoUrl?: string;
}) {
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(false);

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode((prev) => {
      const next = !prev;
      document.documentElement.setAttribute(
        "data-theme",
        next ? "dark" : "light",
      );
      return next;
    });
  }, []);

  const initials = useMemo(() => {
    const words = storeName.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0]![0]! + words[1]![0]!).toUpperCase();
    }
    return (words[0]?.[0] ?? "").toUpperCase();
  }, [storeName]);

  const currentPath = location.pathname.split("/").pop();
  const allStepsIndex = product.steps.findIndex((s) => s.path === currentPath);

  const { items: navItems, stepToNavIndex } = useMemo(
    () => deriveNavItems(product.steps),
    [product.steps],
  );

  const navCurrentIndex = currentPath
    ? (stepToNavIndex.get(currentPath) ?? -1)
    : -1;

  const visibleSteps = useMemo(
    () => product.steps.filter((s) => !s.hideFromNav && !s.shouldSkip?.()),
    [product.steps],
  );
  const progressStepIndex = visibleSteps.findIndex(
    (s) => s.path === currentPath,
  );
  const lastVisibleStepIndex = product.steps.reduce(
    (max, s, i) => (!s.hideFromNav && !s.shouldSkip?.() ? i : max),
    -1,
  );
  const isPastLastNavItem =
    navCurrentIndex === -1 && allStepsIndex > lastVisibleStepIndex;
  const showAskRedo =
    product.id === "returns" || product.id === "order-editing";
  const { toggle } = useAiSlidePanel();
  const toggleAskRedo = useCallback(() => toggle(), [toggle]);

  return (
    <header className={styles.header}>
      <div className={styles.navBar}>
        <div className={styles.brandGroup}>
          <div className={styles.profileAvatar}>
            {logoUrl ? (
              <img
                alt={storeName}
                className={styles.profileImage}
                src={logoUrl}
              />
            ) : (
              <span className={styles.profileInitials}>{initials}</span>
            )}
          </div>
          <span className={styles.storeName}>{storeName}</span>
          <div className={styles.productTag}>
            {product.icon && (
              <product.icon className={styles.productIcon} size={14} />
            )}
            <span className={styles.productName}>{product.label}</span>
          </div>
        </div>

        {navItems.length > 1 && (
          <div className={styles.stepLabels}>
            {navItems.map((item, i) => {
              const reached = isPastLastNavItem || i <= navCurrentIndex;
              return (
                <Fragment key={item.key}>
                  {i > 0 && (
                    <ChevronRight
                      className={classNames(styles.chevron, {
                        [styles.chevronActive!]: reached,
                      })}
                    />
                  )}
                  <span
                    className={classNames(styles.stepLabel, {
                      [styles.stepLabelActive!]: reached,
                    })}
                  >
                    {item.label}
                  </span>
                </Fragment>
              );
            })}
          </div>
        )}

        <div className={styles.rightGroup}>
          <div className={styles.themeTag} onClick={toggleDarkMode}>
            {isDarkMode ? (
              <Sun className={styles.themeIcon} />
            ) : (
              <Moon className={styles.themeIcon} />
            )}
          </div>
          {showAskRedo && (
            <button
              className={styles.askRedoTag}
              onClick={toggleAskRedo}
              type="button"
            >
              <AiStarIcon className={styles.askRedoIcon} />
              <span className={styles.askRedoText}>Ask Redo</span>
            </button>
          )}
          <ExitOnboardingButton
            className={styles.exitTag}
            textClassName={styles.exitText}
          />
        </div>
      </div>
      {visibleSteps.length > 1 ? (
        <div className={styles.progressTrack}>
          <div
            className={styles.progressFill}
            style={{
              width: `${((isPastLastNavItem ? visibleSteps.length : progressStepIndex + 1) / visibleSteps.length) * 100}%`,
            }}
          />
        </div>
      ) : (
        <div className={styles.divider} />
      )}
    </header>
  );
});
